import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";
import type { ProductListResponse } from "@/types/api";

export const DEFAULT_PAGE_SIZE = 12;

export type ProductSort = "popular" | "price-asc" | "price-desc" | "name" | "stock" | "discount";

export type ProductFilter = "new" | "sale" | "hit";

const PRODUCT_SORTS: readonly ProductSort[] = [
  "popular",
  "price-asc",
  "price-desc",
  "name",
  "stock",
  "discount",
];

/** Coerce an untrusted URL value into a valid ProductSort, defaulting to 'popular'. */
export function parseSort(value: string | null): ProductSort {
  return value && (PRODUCT_SORTS as readonly string[]).includes(value)
    ? (value as ProductSort)
    : "popular";
}

export interface ProductsParams {
  /** Catalog section at any depth; the server includes its subsections. */
  sectionId?: string;
  search?: string;
  filter?: ProductFilter;
  sort?: ProductSort;
  page?: number;
  pageSize?: number;
}

/**
 * Server-paginated product listing. Scoped by catalog section and/or a
 * free-text search query, sorted and paged on the server. `keepPreviousData`
 * keeps the current page visible while the next one loads.
 */
export const useProducts = (params: ProductsParams, enabled = true) => {
  const query = {
    ...params,
    search: params.search?.trim() || undefined,
    sort: params.sort ?? "popular",
    page: params.page ?? 1,
    pageSize: params.pageSize ?? DEFAULT_PAGE_SIZE,
  };

  return useQuery<ProductListResponse>({
    queryKey: ["products", query] as const,
    queryFn: () => apiClient.get<ProductListResponse>("/products", query),
    placeholderData: keepPreviousData,
    enabled,
  });
};
